import * as Sentry from "@sentry/remix";
/**
 * By default, Remix will handle hydrating your app on the client for you.
 * You are free to delete this file if you'd like to, but if you ever want it revealed again, you can run `npx remix reveal` ✨
 * For more information, see https://remix.run/file-conventions/entry.client
 */
import i18next from "i18next";
import Fetch from "i18next-fetch-backend";
import { startTransition, StrictMode, useEffect } from "react";
import { hydrateRoot } from "react-dom/client";
import { I18nextProvider, initReactI18next } from "react-i18next";
import { useLocation, useMatches } from "react-router";
import { HydratedRouter } from "react-router/dom";

import { namespaces } from "~/i18n-namespaces";

const dsn = document.querySelector("#sentryDsn")?.getAttribute("data-value");
if (dsn && import.meta.env.PROD) {
  Sentry.init({
    dsn,
    tracesSampleRate: 1,

    integrations: [
      Sentry.browserTracingIntegration({
        useEffect,
        useLocation,
        useMatches,
      }),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
      Sentry.browserProfilingIntegration(),
    ],

    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1,
  });
}

const { supportedLngs, fallbackLng } = window.__i18n ?? {
  supportedLngs: ["en", "zh"],
  fallbackLng: "zh",
};

const currentLang =
  document.querySelector("html")?.getAttribute("lang") ?? fallbackLng;

const initClientModules = async function (): Promise<void> {
  await i18next
    .use(initReactI18next) // Tell i18next to use the react-i18next plugin
    .use(Fetch) // Tell i18next to use the Fetch backend
    .init({
      defaultNS: "common",
      fallbackLng,
      supportedLngs,
      lng: currentLang,
      ns: namespaces,
      backend: {
        // We will configure the backend to fetch the translations from the
        // resource route /api/locales and pass the lng and ns as search params
        loadPath: "/locales/{{lng}}/{{ns}}.json",
      },
    });
};

const [_] = location.pathname.split("/");

initClientModules().then(() => {
  startTransition(() => {
    hydrateRoot(
      document,
      <StrictMode>
        <I18nextProvider i18n={i18next}>
          <HydratedRouter />
        </I18nextProvider>
      </StrictMode>,
    );

    window._isHydrated = true;
  });
});
